import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { User, BookOpen, Briefcase, Users, Upload, ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';

const steps = [
  { id: 1, label: 'Personal',    icon: User },
  { id: 2, label: 'Education',   icon: BookOpen },
  { id: 3, label: 'Experience',  icon: Briefcase },
  { id: 4, label: 'Family',      icon: Users },
  { id: 5, label: 'Documents',   icon: Upload }
];

const inputClass = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const CandidateRegister = () => {
  const navigate       = useNavigate();
  const [step, setStep]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone]    = useState(false);
  const [employeeCode, setEmployeeCode] = useState('');

  const [form, setForm] = useState({
    // Personal
    first_name: '', last_name: '', email: '', phone: '',
    date_of_birth: '', gender: '', marital_status: '',
    blood_group: '', aadhar_number: '', pan_number: '',
    address_current: '', address_permanent: '',
    city: '', state: '', pincode: '',
    worker_category: 'blue_collar',
    total_experience: 0, expected_ctc: '', notice_period: '',
    // Education
    degree: '', institution: '', year_of_passing: '', percentage: '',
    // Experience
    company_name: '', designation: '', from_date: '', to_date: '',
    last_ctc: '', reason_for_leaving: '',
    // Family
    father_name: '', mother_name: '', emergency_contact: '',
    emergency_phone: '', spouse_name: '', children_count: ''
  });

  const [files, setFiles] = useState({ resume: null, photo: null });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleFile = (e) => setFiles({ ...files, [e.target.name]: e.target.files[0] });

  const handleSubmit = async () => {
    if (!files.resume) { toast.error('Please upload your resume'); return; }
    if (!files.photo)  { toast.error('Please upload your photo'); return; }

    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) formData.append(k, v); });
      formData.append('resume', files.resume);
      formData.append('photo',  files.photo);

      const res = await axios.post('/api/v1/candidate-portal/register', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setEmployeeCode(res.data.employee_code);
      setDone(true);
      toast.success('Registration successful!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-md p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Registration Successful!</h2>
        <p className="text-gray-500 mb-4">Your profile has been submitted for review.</p>
        <div className="bg-blue-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-gray-500 mb-1">Your Employee Code</p>
          <p className="text-2xl font-bold text-blue-600">{employeeCode}</p>
          <p className="text-xs text-gray-400 mt-1">Save this code for future reference</p>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Check your email for login credentials. Our team will review your profile shortly.
        </p>
        <button
          onClick={() => navigate('/candidate/login')}
          className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">
          Go to Login
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">M</span>
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Manpower Platform</p>
              <p className="text-xs text-gray-400">Candidate Registration</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/candidate/login')}
            className="text-sm text-blue-600 hover:underline">
            Already registered? Login
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className="flex flex-col items-center gap-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition
                  ${step === s.id ? 'bg-blue-600 text-white' :
                    step > s.id  ? 'bg-green-500 text-white' :
                    'bg-gray-100 text-gray-400'}`}>
                  {step > s.id
                    ? <CheckCircle size={18} />
                    : <s.icon size={18} />}
                </div>
                <span className={`text-xs font-medium ${step === s.id ? 'text-blue-600' : 'text-gray-400'}`}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-green-400' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">

          {/* ── STEP 1: PERSONAL ── */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>First Name *</label>
                  <input name="first_name" value={form.first_name} onChange={handle} required
                    className={inputClass} placeholder="e.g. Ravi" />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input name="last_name" value={form.last_name} onChange={handle}
                    className={inputClass} placeholder="e.g. Kumar" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Phone *</label>
                  <input name="phone" value={form.phone} onChange={handle} required
                    className={inputClass} placeholder="9xxxxxxxxx" />
                </div>
                <div>
                  <label className={labelClass}>Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handle} required
                    className={inputClass} placeholder="ravi@email.com" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Date of Birth</label>
                  <input name="date_of_birth" type="date" value={form.date_of_birth} onChange={handle}
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Gender</label>
                  <select name="gender" value={form.gender} onChange={handle} className={inputClass}>
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Marital Status</label>
                  <select name="marital_status" value={form.marital_status} onChange={handle} className={inputClass}>
                    <option value="">Select</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Blood Group</label>
                  <select name="blood_group" value={form.blood_group} onChange={handle} className={inputClass}>
                    <option value="">Select</option>
                    {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => (
                      <option key={bg} value={bg}>{bg}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Aadhar Number</label>
                  <input name="aadhar_number" value={form.aadhar_number} onChange={handle}
                    className={inputClass} placeholder="12-digit Aadhar" maxLength={12} />
                </div>
                <div>
                  <label className={labelClass}>PAN Number</label>
                  <input name="pan_number" value={form.pan_number} onChange={handle}
                    className={inputClass} placeholder="e.g. ABCDE1234F" maxLength={10} />
                </div>
              </div>

              <div>
                <label className={labelClass}>Current Address</label>
                <textarea name="address_current" value={form.address_current} onChange={handle}
                  className={inputClass} rows={2} placeholder="Door No, Street, Area..." />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>City</label>
                  <input name="city" value={form.city} onChange={handle}
                    className={inputClass} placeholder="Chennai" />
                </div>
                <div>
                  <label className={labelClass}>State</label>
                  <input name="state" value={form.state} onChange={handle}
                    className={inputClass} placeholder="Tamil Nadu" />
                </div>
                <div>
                  <label className={labelClass}>Pincode</label>
                  <input name="pincode" value={form.pincode} onChange={handle}
                    className={inputClass} placeholder="600001" maxLength={6} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Worker Category *</label>
                  <select name="worker_category" value={form.worker_category} onChange={handle} className={inputClass}>
                    <option value="blue_collar">Blue Collar</option>
                    <option value="white_collar">White Collar</option>
                    <option value="specialized">Specialized</option>
                    <option value="technical">Technical</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Total Experience (years)</label>
                  <input name="total_experience" type="number" value={form.total_experience} onChange={handle}
                    className={inputClass} min="0" max="50" step="0.5" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Expected CTC (₹/month)</label>
                  <input name="expected_ctc" type="number" value={form.expected_ctc} onChange={handle}
                    className={inputClass} placeholder="15000" />
                </div>
                <div>
                  <label className={labelClass}>Notice Period (days)</label>
                  <input name="notice_period" type="number" value={form.notice_period} onChange={handle}
                    className={inputClass} placeholder="30" />
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: EDUCATION ── */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Education Details</h2>
              <p className="text-sm text-gray-400 mb-4">Enter your highest qualification</p>

              <div>
                <label className={labelClass}>Degree / Qualification *</label>
                <input name="degree" value={form.degree} onChange={handle}
                  className={inputClass} placeholder="e.g. B.E Mechanical, 10th, ITI, Diploma" />
              </div>

              <div>
                <label className={labelClass}>Institution / School / College *</label>
                <input name="institution" value={form.institution} onChange={handle}
                  className={inputClass} placeholder="e.g. Anna University, Chennai" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Year of Passing</label>
                  <input name="year_of_passing" type="number" value={form.year_of_passing} onChange={handle}
                    className={inputClass} placeholder="2020" min="1990" max="2030" />
                </div>
                <div>
                  <label className={labelClass}>Percentage / CGPA</label>
                  <input name="percentage" type="number" value={form.percentage} onChange={handle}
                    className={inputClass} placeholder="75.5" min="0" max="100" step="0.1" />
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 mt-4">
                <p className="text-sm text-blue-700 font-medium">📝 Note</p>
                <p className="text-sm text-blue-600 mt-1">
                  You can add more education certificates by uploading them in the Documents step.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP 3: EXPERIENCE ── */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Work Experience</h2>
              <p className="text-sm text-gray-400">Enter your most recent work experience (if any)</p>

              <div>
                <label className={labelClass}>Company Name</label>
                <input name="company_name" value={form.company_name} onChange={handle}
                  className={inputClass} placeholder="e.g. ABC Manufacturing Ltd" />
              </div>

              <div>
                <label className={labelClass}>Designation / Role</label>
                <input name="designation" value={form.designation} onChange={handle}
                  className={inputClass} placeholder="e.g. Machine Operator, Welder" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input name="from_date" type="date" value={form.from_date} onChange={handle}
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>To Date</label>
                  <input name="to_date" type="date" value={form.to_date} onChange={handle}
                    className={inputClass} />
                  <p className="text-xs text-gray-400 mt-1">Leave empty if currently working</p>
                </div>
              </div>

              <div>
                <label className={labelClass}>Last Drawn Salary (₹/month)</label>
                <input name="last_ctc" type="number" value={form.last_ctc} onChange={handle}
                  className={inputClass} placeholder="12000" />
              </div>

              <div>
                <label className={labelClass}>Reason for Leaving</label>
                <input name="reason_for_leaving" value={form.reason_for_leaving} onChange={handle}
                  className={inputClass} placeholder="e.g. Better opportunity, Contract ended" />
              </div>

              <div className="bg-amber-50 rounded-xl p-4 mt-2">
                <p className="text-sm text-amber-700">
                  No work experience? That is okay! Skip this step and click Next.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP 4: FAMILY ── */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Family Background</h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Father's Name</label>
                  <input name="father_name" value={form.father_name} onChange={handle}
                    className={inputClass} placeholder="Father's full name" />
                </div>
                <div>
                  <label className={labelClass}>Mother's Name</label>
                  <input name="mother_name" value={form.mother_name} onChange={handle}
                    className={inputClass} placeholder="Mother's full name" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Spouse Name</label>
                  <input name="spouse_name" value={form.spouse_name} onChange={handle}
                    className={inputClass} placeholder="Spouse's full name (if married)" />
                </div>
                <div>
                  <label className={labelClass}>Number of Children</label>
                  <input name="children_count" type="number" value={form.children_count} onChange={handle}
                    className={inputClass} placeholder="0" min="0" />
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Emergency Contact</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Contact Person Name *</label>
                    <input name="emergency_contact" value={form.emergency_contact} onChange={handle}
                      className={inputClass} placeholder="Name of person to contact" />
                  </div>
                  <div>
                    <label className={labelClass}>Contact Phone *</label>
                    <input name="emergency_phone" value={form.emergency_phone} onChange={handle}
                      className={inputClass} placeholder="9xxxxxxxxx" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 5: DOCUMENTS ── */}
          {step === 5 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Documents</h2>

              {/* Resume Upload */}
              <div className={`border-2 border-dashed rounded-xl p-5 text-center transition
                ${files.resume ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-blue-300'}`}>
                <Upload size={24} className={`mx-auto mb-2 ${files.resume ? 'text-green-500' : 'text-gray-400'}`} />
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Resume / CV <span className="text-red-500">*</span>
                </p>
                <p className="text-xs text-gray-400 mb-3">PDF, DOC, DOCX — Max 5MB</p>
                {files.resume ? (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-sm text-green-700">{files.resume.name}</span>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <span className="text-sm text-blue-600 hover:underline">Click to upload</span>
                    <input name="resume" type="file" accept=".pdf,.doc,.docx"
                      onChange={handleFile} className="hidden" />
                  </label>
                )}
              </div>

              {/* Photo Upload */}
              <div className={`border-2 border-dashed rounded-xl p-5 text-center transition
                ${files.photo ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-blue-300'}`}>
                <Upload size={24} className={`mx-auto mb-2 ${files.photo ? 'text-green-500' : 'text-gray-400'}`} />
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Passport Size Photo <span className="text-red-500">*</span>
                </p>
                <p className="text-xs text-gray-400 mb-3">JPG, PNG — Max 5MB</p>
                {files.photo ? (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-sm text-green-700">{files.photo.name}</span>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <span className="text-sm text-blue-600 hover:underline">Click to upload</span>
                    <input name="photo" type="file" accept=".jpg,.jpeg,.png"
                      onChange={handleFile} className="hidden" />
                  </label>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Summary</p>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>👤 Name: <strong>{form.first_name} {form.last_name}</strong></p>
                  <p>📱 Phone: <strong>{form.phone}</strong></p>
                  <p>📧 Email: <strong>{form.email}</strong></p>
                  <p>🎓 Education: <strong>{form.degree || 'Not entered'}</strong></p>
                  <p>💼 Experience: <strong>{form.total_experience} years</strong></p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                <ChevronLeft size={16} />
                Back
              </button>
            )}
            <div className="flex-1" />
            {step < 5 ? (
              <button
                onClick={() => {
                  if (step === 1 && (!form.first_name || !form.phone || !form.email)) {
                    toast.error('Please fill Name, Phone and Email');
                    return;
                  }
                  setStep(step + 1);
                }}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                {loading ? 'Submitting...' : 'Submit Registration'}
                <CheckCircle size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateRegister;